// Main authentication page component for multi-tenant platform with Cognito
// Requirements: 8.1, 8.2, 8.3, 8.5

import React, { useState, useEffect } from 'react';
import { CheckCircleIcon } from '../common/icons';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { EmailVerificationForm } from './EmailVerificationForm';
import { PasswordResetForm } from './PasswordResetForm';
import { CognitoAuthService } from '../../services/cognitoAuthService';
import './AuthenticationPage.css';

interface AuthenticationPageProps {
  onAuthSuccess: (user: any) => void;
  initialMode?: 'login' | 'register' | 'verify' | 'reset';
}

export const AuthenticationPage: React.FC<AuthenticationPageProps> = ({
  onAuthSuccess,
  initialMode = 'login'
}) => {
  const [mode, setMode] = useState<'login' | 'register' | 'verify' | 'reset'>(initialMode);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string>('');

  // Check if user is already authenticated on component mount
  useEffect(() => {
    const checkAuthentication = async () => {
      if (CognitoAuthService.isAuthenticated()) {
        const isValid = await CognitoAuthService.validateToken();
        if (isValid) {
          const user = CognitoAuthService.getCurrentUser();
          if (user) {
            onAuthSuccess(user);
          }
        }
      }
    };

    checkAuthentication();
  }, [onAuthSuccess]);

  // Setup automatic token refresh
  useEffect(() => {
    CognitoAuthService.setupTokenRefresh();
  }, []);

  const handleLoginSuccess = (user: any) => {
    setLoading(false);
    onAuthSuccess(user);
  };

  const handleRegistrationSuccess = (message: string) => {
    setLoading(false);
    
    // For Cognito, registration requires email verification
    // Extract email from the message or use a different approach
    const user = CognitoAuthService.getCurrentUser();
    if (user && CognitoAuthService.isAuthenticated()) {
      // User is authenticated (shouldn't happen with Cognito registration)
      onAuthSuccess(user);
    } else {
      // Show success message and switch to verification if email is available
      setSuccessMessage(message);
      
      // Try to extract email from the registration form or store it separately
      // For now, we'll show the message and let user manually go to verification
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    }
  };

  const handleVerificationSuccess = () => {
    setSuccessMessage('Email verified successfully! You can now sign in.');
    setTimeout(() => {
      setMode('login');
      setSuccessMessage('');
      setPendingVerificationEmail('');
    }, 2000);
  };

  const handlePasswordResetSuccess = () => {
    setSuccessMessage('Password reset successfully! You can now sign in with your new password.');
    setTimeout(() => {
      setMode('login');
      setSuccessMessage('');
    }, 2000);
  };

  const handleSwitchToLogin = () => {
    setMode('login');
    setSuccessMessage('');
    setPendingVerificationEmail('');
  };

  const handleSwitchToRegister = () => {
    setMode('register');
    setSuccessMessage('');
  };

  const handleSwitchToVerify = (email?: string) => {
    setMode('verify');
    if (email) {
      setPendingVerificationEmail(email);
    }
    setSuccessMessage('');
  };

  const handleSwitchToReset = () => {
    setMode('reset');
    setSuccessMessage('');
  };

  const renderCurrentForm = () => {
    switch (mode) {
      case 'register':
        return (
          <RegisterForm
            onRegistrationSuccess={handleRegistrationSuccess}
            onSwitchToLogin={handleSwitchToLogin}
            loading={loading}
          />
        );
      
      case 'verify':
        return (
          <EmailVerificationForm
            email={pendingVerificationEmail}
            onVerificationSuccess={handleVerificationSuccess}
            onBackToLogin={handleSwitchToLogin}
          />
        );
      
      case 'reset':
        return (
          <PasswordResetForm
            onResetSuccess={handlePasswordResetSuccess}
            onBackToLogin={handleSwitchToLogin}
          />
        );
      
      default:
        return (
          <LoginForm
            onLoginSuccess={handleLoginSuccess}
            onSwitchToRegister={handleSwitchToRegister}
            loading={loading}
          />
        );
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__container">
        <div className="auth-page__brand">
          <div className="auth-page__logo">
            <h1>OptiNode AI</h1>
          </div>
          <p className="auth-page__tagline">
            Intelligent Warehouse Optimization Platform
          </p>
        </div>

        <div className="auth-page__form-container">
          {successMessage && (
            <div className="auth-page__success" role="alert">
              <CheckCircleIcon width={20} height={20} className="auth-page__success-icon" />
              {successMessage}
            </div>
          )}

          {renderCurrentForm()}

          {/* Additional navigation links */}
          {mode === 'login' && (
            <div className="auth-page__additional-links">
              <button
                type="button"
                className="auth-page__link-button"
                onClick={handleSwitchToReset}
              >
                Forgot your password?
              </button>
              <button
                type="button"
                className="auth-page__link-button"
                onClick={() => handleSwitchToVerify()}
              >
                Need to verify your email?
              </button>
            </div>
          )}
        </div>

        <div className="auth-page__footer">
          <p>&copy; 2024 OptiNode AI. All rights reserved.</p>
          <div className="auth-page__links">
            <a href="/privacy" className="auth-page__link">Privacy Policy</a>
            <a href="/terms" className="auth-page__link">Terms of Service</a>
            <a href="/support" className="auth-page__link">Support</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationPage;